import React from 'react';
import Select from 'react-select';
import { ReservationTypeOfStay } from '../../../../../shared/enums/reservation-type-of-stay.enum';
import { ReservationChannel } from '../../../../../shared/enums/reservation-channel.enum';
import { ReservationSource } from '../../../../../shared/enums/reservation-souce.enum';
import {
  AssignableReservationSource,
  getAssignableSourceOptions,
  reservationChannelOptionsBySource
} from '../../../../../shared/services/helpers/reservation-source-channel.helper';
import {
  MarketingChannel,
  MessagingChannel,
  marketingChannelOptions,
  messagingChannelOptions
} from '../../../../../shared/interfaces/attribution.interface';
import { LABEL, SECTION_TITLE, selectProps } from '../../reservation-create.ui';

interface Option<T = string> {
  value: T;
  label: string;
}

export interface SourceValues {
  typeOfStay?: ReservationTypeOfStay;
  source: AssignableReservationSource;
  channel: ReservationChannel;
  companyId: string;
  brokerId?: number;
  marketingChannel?: MarketingChannel;
  messagingChannel?: MessagingChannel;
}

export interface SourceSectionProps {
  data: {
    values: SourceValues;
    companies: Option[];
    brokers: Option<number>[];
    isBrokersLoading: boolean;
  };
  eventHandlers: {
    onChange: (values: SourceValues) => void;
  };
}

const segmentOptions: Option<ReservationTypeOfStay>[] = Object.values(ReservationTypeOfStay).map(
  (value) => ({
    value,
    label: value
      .split('_')
      .map((word, index) => (index ? word.toLowerCase() : word[0] + word.slice(1).toLowerCase()))
      .join(' ')
  })
);

export const isSourceComplete = (values: SourceValues) =>
  !!values.typeOfStay &&
  (values.typeOfStay !== ReservationTypeOfStay.CORPORATES || !!values.companyId) &&
  (values.typeOfStay !== ReservationTypeOfStay.BROKER || !!values.brokerId);

export const SourceSection: React.FC<SourceSectionProps> = ({
  data: { values, companies, brokers, isBrokersLoading },
  eventHandlers: { onChange }
}) => {
  const sourceOptions = getAssignableSourceOptions(values.typeOfStay);
  const channelOptions = reservationChannelOptionsBySource[values.source];

  const changeSegment = (typeOfStay: ReservationTypeOfStay) => {
    const allowedSources = getAssignableSourceOptions(typeOfStay);
    const keepSource = allowedSources.some((option) => option.value === values.source);
    const source = keepSource
      ? values.source
      : (allowedSources[0].value as AssignableReservationSource);
    onChange({
      ...values,
      typeOfStay,
      companyId: typeOfStay === ReservationTypeOfStay.CORPORATES ? values.companyId : '',
      brokerId: typeOfStay === ReservationTypeOfStay.BROKER ? values.brokerId : undefined,
      source,
      channel: keepSource ? values.channel : reservationChannelOptionsBySource[source][0].value
    });
  };

  return (
    <>
      <h2 className={SECTION_TITLE}>Source</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-[12px]">
        <label className={LABEL}>
          Segment
          <Select<Option<ReservationTypeOfStay>, false>
            {...selectProps}
            options={segmentOptions}
            value={segmentOptions.find((option) => option.value === values.typeOfStay) || null}
            onChange={(value) => value && changeSegment(value.value)}
            placeholder="Select segment"
          />
        </label>
        <label className={LABEL}>
          Source
          <Select<Option<ReservationSource>, false>
            {...selectProps}
            options={sourceOptions}
            value={sourceOptions.find((option) => option.value === values.source) || null}
            onChange={(value) => {
              const source = (value?.value || ReservationSource.CX) as AssignableReservationSource;
              onChange({
                ...values,
                source,
                channel: reservationChannelOptionsBySource[source][0].value
              });
            }}
          />
        </label>
        <label className={LABEL}>
          Channel
          <Select<Option<ReservationChannel>, false>
            {...selectProps}
            options={channelOptions}
            value={channelOptions.find((option) => option.value === values.channel) || null}
            onChange={(value) =>
              onChange({ ...values, channel: value?.value || ReservationChannel.BIRDNEST })
            }
          />
        </label>
        {values.typeOfStay === ReservationTypeOfStay.CORPORATES && (
          <label className={LABEL}>
            Company
            <Select<Option, false>
              {...selectProps}
              options={companies}
              value={companies.find((option) => option.value === values.companyId) || null}
              onChange={(value) => onChange({ ...values, companyId: value?.value || '' })}
              placeholder="Select company"
              isClearable
              isSearchable
            />
          </label>
        )}
        {values.typeOfStay === ReservationTypeOfStay.BROKER && (
          <label className={LABEL}>
            Broker
            <Select<Option<number>, false>
              {...selectProps}
              options={brokers}
              value={brokers.find((option) => option.value === values.brokerId) || null}
              onChange={(value) => onChange({ ...values, brokerId: value?.value })}
              placeholder="Select broker"
              isLoading={isBrokersLoading}
              isDisabled={isBrokersLoading}
              isClearable
              isSearchable
            />
          </label>
        )}
        {values.source === ReservationSource.CX && (
          <>
            <label className={LABEL}>
              Marketing channel
              <Select<Option<MarketingChannel>, false>
                {...selectProps}
                options={marketingChannelOptions}
                value={
                  marketingChannelOptions.find(
                    (option) => option.value === values.marketingChannel
                  ) || null
                }
                onChange={(value) => onChange({ ...values, marketingChannel: value?.value })}
                placeholder="Where did the guest come from?"
                isClearable
              />
            </label>
            <label className={LABEL}>
              Messaging channel
              <Select<Option<MessagingChannel>, false>
                {...selectProps}
                options={messagingChannelOptions}
                value={
                  messagingChannelOptions.find(
                    (option) => option.value === values.messagingChannel
                  ) || null
                }
                onChange={(value) => onChange({ ...values, messagingChannel: value?.value })}
                placeholder="How did the guest reach us?"
                isClearable
              />
            </label>
          </>
        )}
      </div>
    </>
  );
};
